import { Box, Heading, Tag, Image, Flex } from '@everlywell/ui-kit';
import DynamicIcon, { IconKey } from 'components/DynamicIcon';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import ProductPricingButton from 'components/ProductPricingButton';
import RichTextWrapper from 'components/RichTextWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ImageType } from 'utils/types';
import { CallToAction, ContentfulRichText } from 'utils/types/contentfulTypes';

export type TakeoverBannerProps = {
  eyebrow?: string | null;
  title?: string | null;
  description?: ContentfulRichText | null;
  cta?: CallToAction | null;
  backgroundColor?: string | null;
  desktopImages?: ImageType[] | null;
  mobileImages?: ImageType[] | null;
};

type CtaParts = {
  priceText: string;
  startingText: string;
  billFrequencyText: string;
  icon: string;
};

const TakeoverBanner = ({
  eyebrow,
  title,
  description,
  cta,
  backgroundColor,
  desktopImages,
  mobileImages,
}: TakeoverBannerProps) => {
  const { startingText, priceText, billFrequencyText, icon } = (cta?.metadata
    ?.context?.ctaParts || {}) as CtaParts;

  const trackCtaClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'Takeover Banner',
        ctaText: cta?.label ?? 'No label provided',
      },
    });
  };

  return (
    <SectionContainer
      display={[null, 'null', 'flex']}
      size="fluid"
      backgroundColor={backgroundColor || undefined}
      clipPath="ellipse(180% 60% at 50% 40%)"
    >
      <SectionContainer
        as="div"
        marginX="auto"
        minHeight={[null, null, '700px']}
        overflow="hidden"
        padding="0"
      >
        <Flex
          justifyContent="space-between"
          flexDirection={['column', null, 'row']}
          alignItems="center"
          height="100%"
          width="100%"
          gap={[null, null, 2]}
        >
          {/* Mobile Image (on the top) */}
          {mobileImages && mobileImages[0] ? (
            <Image
              as={GatsbyImage}
              image={mobileImages?.[0].gatsbyImageData}
              alt={mobileImages[0].description}
              objectFit="cover"
              width="100%"
              height="auto"
              display={['flex', null, 'none']}
            />
          ) : null}

          {/* Desktop Image the bottom left */}
          <Box
            display={['none', null, 'block']}
            width="27.5%"
            alignSelf="flex-end"
          >
            {desktopImages && desktopImages[0] ? (
              <Image
                as={GatsbyImage}
                image={desktopImages?.[0].gatsbyImageData}
                alt={desktopImages[0].description}
              />
            ) : null}
          </Box>

          <Box
            width={['100%', null, '45%']}
            maxWidth={[null, null, '550px', '660px']}
            paddingTop="12"
            paddingRight={[6, null, 0]}
            paddingBottom="12"
            paddingLeft={[6, null, 0]}
            marginX="auto"
          >
            {eyebrow ? (
              <Tag
                variant="callout"
                display="flex"
                width="fit-content"
                marginLeft="auto"
                marginRight="auto"
                marginBottom={[4, 6]}
              >
                {eyebrow}
              </Tag>
            ) : null}
            {title ? (
              <Heading
                as="h2"
                size="3xl"
                color="tints.white"
                textAlign="center"
                marginBottom={[3, 4]}
              >
                {title}
              </Heading>
            ) : null}
            {description ? (
              <Box
                sx={{
                  '*': {
                    color: 'tints.white',
                    textAlign: 'center',
                    fontSize: ['md', 'xl'],
                    marginBottom: [5, 10],
                  },
                }}
              >
                <RichTextWrapper
                  contentfulRichText={description}
                ></RichTextWrapper>
              </Box>
            ) : null}
            <Box width={['fit-content']} margin="0 auto">
              {cta ? (
                <ProductPricingButton
                  startingText={startingText}
                  priceText={priceText}
                  size="md"
                  billFrequencyText={billFrequencyText}
                  buttonText={cta.label || ''}
                  colorScheme="greenCS"
                  onButtonClick={trackCtaClick}
                  buttonIconRight={
                    <DynamicIcon icon={(icon as IconKey) || 'ArrowRight'} />
                  }
                  linkTo={cta.url || ''}
                  id="takeover-banner-join-cta"
                />
              ) : null}
            </Box>
          </Box>

          <Box
            display={['none', null, 'block']}
            width="27.5%"
            alignSelf="flex-start"
          >
            {/* Desktop Image (floating on the top right) */}
            {desktopImages && desktopImages[1] ? (
              <Image
                as={GatsbyImage}
                image={desktopImages?.[1].gatsbyImageData}
                alt={desktopImages[1].description}
              />
            ) : null}
          </Box>
        </Flex>
      </SectionContainer>
    </SectionContainer>
  );
};

export default TakeoverBanner;
